/** IMMO search form — compact base criteria + collapsible groups. */
import React from "react";
import DarkSelect from "../home/DarkSelect";
import DarkMultiSelect from "../home/DarkMultiSelect";
import LocationPicker from "../home/LocationPicker";
import { locationFromParams, locationPatch } from "../../lib/searchQuery";
import { Field, NumberSelect, Section, ToggleChip, fmtEur } from "./fields";

const BUY_STEPS = [50000, 75000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 500000, 650000, 800000, 1000000, 1500000, 2000000];
const RENT_STEPS = [400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 2000, 2500, 3000];
const ROOM_STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 6];
const AREA_STEPS = [30, 40, 50, 60, 70, 80, 90, 100, 120, 140, 160, 180, 200, 250, 300];
const YB_STEPS = [2020, 2015, 2010, 2000, 1990, 1980, 1970, 1960, 1945, 1919];

export default function ImmoSearchForm({ params, set, fo, t, foStatus = {}, locText = "", setLocText }) {
  const objectTypeOpts = (fo.object_types || []).map((o) => ({
    value: o.value,
    label: `${t(`enums:object_type.${o.value}`, { defaultValue: o.value })} (${o.count})`,
  }));
  const anyObjectLabel = foStatus.loading && objectTypeOpts.length === 0
    ? t("search.loadingOptions")
    : foStatus.error && objectTypeOpts.length === 0
      ? t("search.optionsError")
      : t("hero.anyObjectType");
  const tx = params.transaction_type === "rent" ? "rent" : "buy";
  const priceSteps = tx === "rent" ? RENT_STEPS : BUY_STEPS;
  const featureOpts = (fo.features || []).map((f) => ({
    value: f.value,
    label: `${t(`enums:features.${f.value}`, { defaultValue: f.value })} (${f.count})`,
  }));
  const selFeatures = params.features ? String(params.features).split(",") : [];
  const cnt = (keys) => keys.filter((k) => params[k]).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("hero.transaction")}>
          <div className="flex gap-2">
            {["buy", "rent"].map((v) => (
              <ToggleChip
                key={v}
                active={tx === v}
                testid={`search-tx-${v}`}
                onClick={() => set({ transaction_type: v, price_min: null, price_max: null })}
              >
                {v === "buy" ? t("hero.buy") : t("hero.rentTx")}
              </ToggleChip>
            ))}
          </div>
        </Field>
        <Field label={t("hero.objectType")}>
          <DarkSelect
            value={params.object_type || ""}
            testid="search-object-type"
            options={[{ value: "", label: anyObjectLabel }, ...objectTypeOpts]}
            onChange={(v) => set({ object_type: v || null })}
          />
        </Field>
        <Field label={t("hero.location")}>
          <LocationPicker
            vertical="immo"
            country={params.country}
            testid="search-location"
            value={locationFromParams(params)}
            text={locText}
            onTextChange={setLocText}
            onChange={(loc) => set(locationPatch(loc))}
          />
        </Field>
        <Field label={tx === "rent" ? t("search.rentTo") : t("search.priceTo")}>
          <NumberSelect
            value={params.price_max}
            steps={priceSteps}
            anyLabel={t("hero.any")}
            customLabel={t("search.customValue")}
            format={fmtEur}
            testid="search-price-max"
            onChange={(v) => set({ price_max: v })}
          />
        </Field>
        <Field label={t("search.roomsFrom")}>
          <NumberSelect value={params.rooms_min} steps={ROOM_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-rooms-min" onChange={(v) => set({ rooms_min: v })} />
        </Field>
        <Field label={t("search.areaFrom")}>
          <NumberSelect value={params.area_min} steps={AREA_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={(n) => `${n} m²`} testid="search-area-min" onChange={(v) => set({ area_min: v })} />
        </Field>
      </div>

      <Section
        title={t("search.groupObject")}
        testid="search-group-object"
        count={cnt(["price_min", "rooms_max", "area_max", "year_built_min", "year_built_max"])}
      >
        <Field label={tx === "rent" ? t("search.rentFrom") : t("search.priceFrom")}>
          <NumberSelect value={params.price_min} steps={priceSteps} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={fmtEur} testid="search-price-min" onChange={(v) => set({ price_min: v })} />
        </Field>
        <Field label={t("search.roomsTo")}>
          <NumberSelect value={params.rooms_max} steps={ROOM_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-rooms-max" onChange={(v) => set({ rooms_max: v })} />
        </Field>
        <Field label={t("search.areaTo")}>
          <NumberSelect value={params.area_max} steps={AREA_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={(n) => `${n} m²`} testid="search-area-max" onChange={(v) => set({ area_max: v })} />
        </Field>
        <Field label={t("search.yearBuiltFrom")}>
          <NumberSelect value={params.year_built_min} steps={[...YB_STEPS].reverse()} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-yb-min" onChange={(v) => set({ year_built_min: v })} />
        </Field>
        <Field label={t("search.yearBuiltTo")}>
          <NumberSelect value={params.year_built_max} steps={YB_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-yb-max" onChange={(v) => set({ year_built_max: v })} />
        </Field>
      </Section>

      {(featureOpts.length > 0 || foStatus.loading || foStatus.error) && (
      <Section title={t("search.groupImmoFeatures")} testid="search-group-features" count={selFeatures.length}>
        <div className="col-span-full">
          <Field label={t("hero.features")}>
            <DarkMultiSelect
              values={selFeatures}
              testid="search-features"
              options={featureOpts}
              placeholder={t("search.anyFeatures")}
              searchPlaceholder={t("search.searchFeature")}
              selectedLabel={(n) => t("search.nSelected", { count: n })}
              resetLabel={t("search.reset")}
              onChange={(vals) => set({ features: vals.length ? vals.join(",") : null })}
              loading={!!foStatus.loading}
              error={!!foStatus.error}
              onRetry={foStatus.retry}
              loadingLabel={t("search.loadingOptions")}
              errorLabel={t("search.optionsError")}
              retryLabel={t("search.retry")}
            />
          </Field>
        </div>
      </Section>
      )}

      <Section
        title={t("search.groupProvider")}
        testid="search-group-provider"
        count={cnt(["seller_type", "yield_min", "price_reduced", "new_days", "rated"]) + (params.quality === "deals" ? 1 : 0)}
      >
        <div className="col-span-full flex flex-wrap gap-2">
          <ToggleChip active={!!params.price_reduced} testid="search-price-reduced" onClick={() => set({ price_reduced: params.price_reduced ? null : true })}>
            {t("quick.priceReduced")}
          </ToggleChip>
          <ToggleChip active={!!params.new_days} testid="search-new-days" onClick={() => set({ new_days: params.new_days ? null : 7 })}>
            {t("quick.newDaysChip")}
          </ToggleChip>
          <ToggleChip active={!!params.rated} testid="search-rated" onClick={() => set({ rated: params.rated ? null : true })}>
            {t("search.ratedOnly")}
          </ToggleChip>
          <ToggleChip active={params.quality === "deals"} testid="search-deals-only" onClick={() => set({ quality: params.quality === "deals" ? null : "deals" })}>
            {t("quick.dealsOnly")}
          </ToggleChip>
          {tx === "buy" && (
            <ToggleChip active={!!params.yield_min} testid="search-yield" onClick={() => set({ yield_min: params.yield_min ? null : 4 })}>
              {t("quick.yieldMin")}
            </ToggleChip>
          )}
        </div>
        <Field label={t("hero.sellerType")}>
          <DarkSelect
            value={params.seller_type || ""}
            testid="search-seller-type"
            options={[
              { value: "", label: t("hero.any") },
              { value: "private", label: t("hero.seller_private") },
              { value: "broker", label: t("hero.seller_broker") },
              { value: "developer", label: t("hero.seller_developer") },
            ]}
            onChange={(v) => set({ seller_type: v || null })}
          />
        </Field>
      </Section>
    </div>
  );
}
